<template>
  <div class="pa-4">
    <!-- Filters -->
    <v-card class="pa-4 mb-6" elevation="0" outlined>
      <v-row>
        <v-col cols="12">
          <div class="v-label mb-2">{{ filterLabel }}</div>
          <div v-if="filterItemsLoading" class="text-center">載入中...</div>
          <v-card v-else class="pa-2" style="max-height: 200px; overflow-y: auto;" outlined>
            <v-checkbox
              v-model="selectAll"
              label="全選"
              hide-details
              dense
              class="ma-0 pa-0"
            ></v-checkbox>
            <v-divider class="my-4"/>
            <div class="d-flex flex-wrap">
              <v-checkbox
                v-for="item in filterItems"
                :key="item[itemValue]"
                v-model="selectedItems"
                :label="item[itemText]"
                :value="item[itemValue]"
                hide-details
                dense
                class="ma-0 pa-0 mr-4"
              ></v-checkbox>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-card>

    <!-- Chart Sections -->
    <v-row v-if="selectedItems.length > 0">
      <v-col v-for="(chart, key) in doctorCharts" :key="key" cols="12">
        <chart-section-v2
          :title="chart.title"
          :loading="chart.loading"
          :available-charts="chart.availableCharts"
          :active-key="chart.activeKey"
          :metric-groups="chart.metricGroups"
          @update:metric-group-selection="({ groupKey, value }) => handleMetricGroupSelection(key, groupKey, value)"
          @update:active-key="newType => handleActiveKeyUpdate(key, newType)"
        ></chart-section-v2>
      </v-col>
    </v-row>
    <v-card v-else height="400" class="d-flex justify-center align-center" outlined>
      <p class="text-h6 grey--text">{{ emptyText }}</p>
    </v-card>
  </div>
</template>

<script>
import {
  getDoctorListAPI,
  getClinicListAPI,
  getAppointmentsByDoctorApi,
  getAppointmentsByDoctorSourceApi,
  getAppointmentsByDoctorProcessingResultApi,
  getAppointmentsByDoctorAppointmentItemApi,
  getAppointmentsByClinicApi,
  getAppointmentsByClinicSourceApi,
  getAppointmentsByClinicProcessingResultApi,
  getAppointmentsByClinicAppointmentItemApi
} from "@/api";
import { mapGetters } from "vuex";
import ChartSectionV2 from "@/components/analytics/ChartSectionV2.vue";
import {
  buildDoctorAppointmentItemLineChart, buildDoctorAppointmentItemMixedChart,
  buildDoctorProcessingResultLineChartData, buildDoctorProcessingResultMixedChart,
  buildDoctorSourceLineChartData, buildDoctorSourceMixedChart,
  buildDoctorTotalAppointmentMixedChart, buildDoctorTotalAppointmentLineChartData,
} from "@/utils/analysisToolRefactor";

// —— 共用常數：quantity 群組只寫一次 ——
const QUANTITY_GROUP = {
  key: 'quantity',
  label: '指標',
  items: [
    { text: '預約數量', value: 'appointments' },
    { text: 'NP數量', value: 'newPatients' },
    { text: 'NP比例', value: 'npRatio' },
  ],
  selected: 'appointments',
  show: false,
};

// —— 區塊工廠：建立 {data, loading, activeKey, metricGroups} 的一致結構 ——
function createSection(extraGroups = []) {
  return {
    data: {},
    loading: false,
    activeKey: 'mixed',
    metricGroups: [
      // 深拷貝，避免互相污染
      JSON.parse(JSON.stringify(QUANTITY_GROUP)),
      ...extraGroups.map(g => ({ ...g, show: false })),
    ],
  };
}

// —— 取出/設定群組的小工具 ——
function getGroup(sectionState, key) {
  return sectionState.metricGroups.find(g => g.key === key);
}

function setGroupSelected(sectionState, key, value) {
  const g = getGroup(sectionState, key);
  if (g) g.selected = value;
}

// 將群組轉成「會依 activeKey 控制 show」的 ViewModel
function toViewGroup(sectionState, key, showWhenActiveKeys) {
  const g = getGroup(sectionState, key);
  const show = Array.isArray(showWhenActiveKeys)
    ? showWhenActiveKeys.includes(sectionState.activeKey)
    : sectionState.activeKey === showWhenActiveKeys;
  // 僅映射必要欄位，避免 computed 製造新參考導致不必要重渲染
  return {
    key: g.key,
    label: g.label,
    items: g.items,
    selected: g.selected,
    show,
  };
}

async function fetchSection(sectionState, selectApiFn, params, onSuccess) {
  sectionState.loading = true;
  try {
    const api = selectApiFn();
    const res = await api(params);
    sectionState.data = res.data;
    if (onSuccess) onSuccess(res.data, sectionState);
  } catch (e) {
    console.error("fetch section failed:", e);
  } finally {
    sectionState.loading = false;
  }
}

// 你原本的 this.clinicUser 判斷可以包成：
function pickApi(isClinic, doctorApi, clinicApi) {
  return () => (isClinic ? doctorApi : clinicApi);
}


export default {
  name: 'OnlineDetailAnalysisTab',
  components: {ChartSectionV2},
  props: {
    dateRange: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      filterItemsLoading: false,
      filterItems: [],
      selectedItems: [],

      // A. 總覽
      appointments: createSection(),

      // B. 來源（多一個 source 群組）
      sourceAnalysis: createSection([
        { key: 'source', label: '預約來源', items: [], selected: null },
      ]),

      // C. 處理結果（多一個 result 群組）
      resultAnalysis: createSection([
        { key: 'result', label: '處理結果', items: [], selected: null },
      ]),

      // D. 項目（多一個 appointmentItem 群組）
      appointmentItemAnalysis: createSection([
        { key: 'appointmentItem', label: '預約項目', items: [], selected: null },
      ]),
    }
  },
  computed: {
    ...mapGetters(['axiosParamData', 'clinicUser']),
    isClinicLogin() {
      return this.clinicUser;
    },
    areaName() {
      return this.clinicUser ? '醫師' : '診所'
    },
    filterLabel() {
      return this.isClinicLogin ? '篩選醫師' : '篩選診所';
    },
    itemText() {
      return this.isClinicLogin ? 'doctor_name' : 'clinic_name';
    },
    itemValue() {
      return this.isClinicLogin ? 'doctor_code' : 'clinic_code';
    },
    emptyText() {
      return this.isClinicLogin ? '請先從上方選擇醫師' : '請先從上方選擇診所';
    },
    selectAll: {
      get() {
        if (!this.filterItems || this.filterItems.length === 0) return false;
        return this.selectedItems.length === this.filterItems.length;
      },
      set(value) {
        if (value) {
          this.selectedItems = this.filterItems.map(item => item[this.itemValue]);
        } else {
          this.selectedItems = [];
        }
      }
    },
    appointmentsChart() {
      return {
        title: `個別${this.areaName}線上預約數`,
        loading: this.appointments.loading,
        activeKey: this.appointments.activeKey,
        metricGroups: [
          toViewGroup(this.appointments, 'quantity', /* 哪些 activeKey 顯示這組？ */ ['line']),
        ],
        ...this.formatDoctorTotalAppointmentsData(this.appointments.data),
      };
    },
    sourceAnalysisChart() {
      return {
        title: `個別${this.areaName}線上預約來源`,
        loading: this.sourceAnalysis.loading,
        activeKey: this.sourceAnalysis.activeKey,
        availableCharts: {},
        metricGroups: [
          toViewGroup(this.sourceAnalysis, 'quantity', 'line2'),
          toViewGroup(this.sourceAnalysis, 'source',   'line2'),
        ],
        ...this.formatDoctorSourceData(this.sourceAnalysis.data),
      };
    },
    resultAnalysisChart() {
      return {
        title: `個別${this.areaName}線上預約處理結果`,
        loading: this.resultAnalysis.loading,
        activeKey: this.resultAnalysis.activeKey,
        availableCharts: {},
        metricGroups: [
          toViewGroup(this.resultAnalysis, 'quantity', 'line'),
          toViewGroup(this.resultAnalysis, 'result',   'line'),
        ],
        ...this.formatDoctorResultData(this.resultAnalysis.data),
      };
    },
    appointmentItemAnalysisChart() {
      return {
        title: `個別${this.areaName}線上預約項目`,
        loading: this.appointmentItemAnalysis.loading,
        activeKey: this.appointmentItemAnalysis.activeKey,
        availableCharts: {},
        metricGroups: [
          toViewGroup(this.appointmentItemAnalysis, 'quantity', 'line'),
          toViewGroup(this.appointmentItemAnalysis, 'appointmentItem',   'line'),
        ],
        ...this.formatDoctorAppointmentItemData(this.appointmentItemAnalysis.data),
      };
    },
    doctorCharts() {
      return {
        appointments: this.appointmentsChart,
        sourceAnalysis: this.sourceAnalysisChart,
        appointmentItemAnalysis: this.appointmentItemAnalysisChart,
        resultAnalysis: this.resultAnalysisChart,
      }
    }
  },
  watch: {
    dateRange: {
      handler() {
        this.fetchAllData();
      },
      immediate: true,
      deep: true
    }
    // selectedItems: 'fetchAllData',
  },
  mounted() {
    this.fetchFilterItems();
  },
  methods: {
    handleMetricGroupSelection(chartKey, groupKey, value) {
      if (this[chartKey]) {
        const metricGroup = this[chartKey].metricGroups.find(group => group.key === groupKey);
        if (metricGroup) {
          metricGroup.selected = value;
        }
      }
    },
    handleActiveKeyUpdate(chartKey, newKey) {
      if (this[chartKey]) {
        this[chartKey].activeKey = newKey;
      }
    },
    async fetchFilterItems() {
      this.filterItemsLoading = true;
      try {
        const apiCall = this.isClinicLogin ? getDoctorListAPI : getClinicListAPI;
        const res = await apiCall(this.axiosParamData);
        this.filterItems = res.data;
        this.selectedItems = this.filterItems.map(d => d[this.itemValue]);
      } catch (error) {
        console.error(`Failed to fetch ${this.isClinicLogin ? 'doctors' : 'clinics'}:`, error);
      } finally {
        this.filterItemsLoading = false;
      }
    },
    async fetchAllData() {
      if (!this.dateRange || this.dateRange.length !== 2) return;
      const [start, end] = [...this.dateRange].sort();
      const params = {
        ...this.axiosParamData,
        query_start: start,
        query_end: end
      }

      this.getAppointmentData(params);
      this.getSourceAnalysisData(params);
      this.getResultAnalysisData(params);
      this.getAppointmentItemAnalysisData(params);
    },
    async getAppointmentData(params) {
      await fetchSection(
        this.appointments,
        pickApi(this.clinicUser, getAppointmentsByDoctorApi, getAppointmentsByClinicApi),
        params
      );
    },
    async getSourceAnalysisData(params) {
      await fetchSection(
        this.sourceAnalysis,
        pickApi(this.clinicUser, getAppointmentsByDoctorSourceApi, getAppointmentsByClinicSourceApi),
        params,
        (data) => {
          // 這裡只處理「來源選單項目」的注入
          const group = getGroup(this.sourceAnalysis, 'source');
          group.items = data?.sourceList
          if (!group.selected && group.items.length) group.selected = group.items[0];
        }
      );
    },
    async getResultAnalysisData(params) {
      await fetchSection(
        this.resultAnalysis,
        pickApi(this.clinicUser, getAppointmentsByDoctorProcessingResultApi, getAppointmentsByClinicProcessingResultApi),
        params,
        (data) => {
          // 這裡只處理「來源選單項目」的注入
          const group = getGroup(this.resultAnalysis, 'result');
          group.items = data?.resultList
          if (!group.selected && group.items.length) group.selected = group.items[0];
        }
      );
    },
    async getAppointmentItemAnalysisData(params) {
      await fetchSection(
        this.appointmentItemAnalysis,
        pickApi(this.clinicUser, getAppointmentsByDoctorAppointmentItemApi, getAppointmentsByClinicAppointmentItemApi),
        params,
        (data) => {
          // 這裡只處理「來源選單項目」的注入
          const group = getGroup(this.appointmentItemAnalysis, 'appointmentItem');
          group.items = data?.appointmentItemList
          if (!group.selected && group.items.length) group.selected = group.items[0];
        }
      );
    },
    formatDoctorTotalAppointmentsData(data) {
      const selectedMetric = this.appointments.metricGroups.find(g => g.key === 'quantity').selected;

      const list = data.data;
      let filteredResult;

      if (this.isClinicLogin) {
        filteredResult = list?.filter(d => this.selectedItems.includes(d.doctor_code));
      } else {
        filteredResult = list?.filter(c => this.selectedItems.includes(c.agency_code));
      }

      if (filteredResult) {
        const opts = { actorHeader: this.isClinicLogin ? '醫師' : '診所' };
        return {
          availableCharts: {
            mixed: buildDoctorTotalAppointmentMixedChart(filteredResult, opts),
            line: buildDoctorTotalAppointmentLineChartData(filteredResult, selectedMetric, {chartName: '預約數走勢'})
          }
        };
      }
    },
    formatDoctorSourceData(data) {

      const selectedQuantityMetric = this.sourceAnalysis.metricGroups.find(g => g.key === 'quantity').selected;
      const selectedSourceMetric = this.sourceAnalysis.metricGroups.find(g => g.key === 'source').selected;

      const list = data.dataList;
      let filteredResult;

      if (this.isClinicLogin) {
        filteredResult = list?.filter(d => this.selectedItems.includes(d.doctor_code));
      } else {
        filteredResult = list?.filter(c => this.selectedItems.includes(c.agency_code));
      }

      if (filteredResult) {
        const opts = { actorHeader: this.isClinicLogin ? '醫師' : '診所', chartName: '來源結構' };

        return {
          availableCharts: {
            mixed: buildDoctorSourceMixedChart(filteredResult, opts),
            // mixed2: buildSourceAxisMixedChart(filteredResult),  // hint: 不需要這個圖，但先留著
            line2: buildDoctorSourceLineChartData(filteredResult, selectedSourceMetric, selectedQuantityMetric, {chartName: '預約走勢'})
          }
        };
      }
    },
    formatDoctorResultData(data) {
      const selectedQuantityMetric = this.resultAnalysis.metricGroups.find(g => g.key === 'quantity').selected;
      const selectedResultMetric = this.resultAnalysis.metricGroups.find(g => g.key === 'result').selected;

      const list = data.dataList;
      let filteredResult;

      if (this.isClinicLogin) {
        filteredResult = list?.filter(d => this.selectedItems.includes(d.doctor_code));
      } else {
        filteredResult = list?.filter(c => this.selectedItems.includes(c.agency_code));
      }

      if (filteredResult) {
        const opts = { actorHeader: this.isClinicLogin ? '醫師' : '診所', chartName: '處理結果結構' };
        return {
          availableCharts: {
            mixed: buildDoctorProcessingResultMixedChart(filteredResult, opts),
            line: buildDoctorProcessingResultLineChartData(filteredResult, selectedResultMetric, selectedQuantityMetric, {chartName: '處理結果走勢'})
          }
        };
      }
    },
    formatDoctorAppointmentItemData(data) {
      const selectedQuantityMetric = this.appointmentItemAnalysis.metricGroups.find(g => g.key === 'quantity').selected;
      const selectedAppointmentItemMetric = this.appointmentItemAnalysis.metricGroups.find(g => g.key === 'appointmentItem').selected;

      const list = data.dataList;
      let filteredResult;

      if (this.isClinicLogin) {
        filteredResult = list?.filter(d => this.selectedItems.includes(d.doctor_code));
      } else {
        filteredResult = list?.filter(c => this.selectedItems.includes(`${c.agency_code}`));
      }

      if (filteredResult) {
        const opts = { actorHeader: this.isClinicLogin ? '醫師' : '診所', chartName: 'NP結構' };

        return {
          availableCharts: {
            mixed: buildDoctorAppointmentItemMixedChart(filteredResult, opts),
            line: buildDoctorAppointmentItemLineChart(filteredResult, selectedAppointmentItemMetric, selectedQuantityMetric, {chartName: '預約走勢'})
          }
        };
      }
    }
  }
}
</script>
